<script setup lang="ts">
import { onMounted, ref, watch, nextTick } from 'vue';
import type { AnnotationCoordinate } from '@/api';
import {
  gaiaFontSize,
  gaiaTracking,
  gaiaFontWeight,
  gaiaFonts,
  gaiaNeutral,
  gaiaShadow,
  gaiaTemplating,
} from '@/components/design/themes/tokens';
import {
  parseBlockTag,
  renderJinjaPreview,
  type AnnotationDraft,
} from '@/composables/contractGeneration/useTemplateAnnotations';

interface Props {
  html: string;
  annotations: AnnotationDraft[];
  /**
   * The chips to light up — every occurrence of the selected field. The first
   * one is scrolled into view whenever the set changes.
   */
  activeLocalIds?: string[];
}

interface Emits {
  (e: 'click-tag', annotation: AnnotationDraft, target: HTMLElement): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const containerRef = ref<HTMLElement | null>(null);

/** Set the preview HTML imperatively (avoids v-html). */
function applyHtml(html: string): void {
  const container = containerRef.value;
  if (!container) return;
  // The HTML comes from our backend's preview endpoint, generated from a
  // trusted .docx — no user-supplied content flows through it.
  container.innerHTML = html;
  // Cache each run's pristine text the moment the backend HTML loads. We
  // restore from this cache before every chip-injection pass so removing a
  // tag (or any subsequent re-render) doesn't lose the surrounding source
  // text. Backend's preview is the single source of truth — we never derive
  // run text from chips/fragments after the fact.
  for (const run of container.querySelectorAll<HTMLElement>('[data-paragraph-index], [data-pid]')) {
    run.dataset.originalText = run.textContent;
  }
}

/** Re-inject chips whenever HTML or annotations change. */
async function syncChips(): Promise<void> {
  await nextTick();
  const container = containerRef.value;
  if (!container) return;

  // Restore each run's pristine text content (from the cache populated in
  // applyHtml) before re-injecting chips. This guarantees that:
  //   1. Removing a chip immediately makes its underlying tag text vanish
  //      from the preview (no need to wait for save).
  //   2. Sibling chips in the same run can still locate their `original_text`
  //      on every re-render — even if one of them was just deleted.
  for (const run of container.querySelectorAll<HTMLElement>('[data-paragraph-index], [data-pid]')) {
    const cached = run.dataset.originalText;
    if (cached !== undefined) run.textContent = cached;
  }

  // Group annotations by paragraph — a single Jinja tag's text can straddle
  // multiple `<r>` runs in the .docx (Word splits runs at formatting
  // boundaries), so we have to know every run that belongs to the same
  // paragraph before we can decide which run the chip lives in and which
  // text segments to claim across the run list.
  const byParagraph = new Map<string, AnnotationDraft[]>();
  for (const annotation of props.annotations) {
    const key = paragraphKey(annotation.anchor);
    const list = byParagraph.get(key);
    if (list) list.push(annotation);
    else byParagraph.set(key, [annotation]);
  }

  for (const [, annotations] of byParagraph) {
    if (annotations.length === 0) continue;
    const runs = findRunsInParagraph(container, annotations[0].anchor);
    if (runs.length === 0) continue;
    placeChipsInParagraph(runs, annotations);
  }
}

function paragraphKey(anchor: AnnotationCoordinate): string {
  const scope = anchor.scope;
  return JSON.stringify({
    kind: scope.kind ?? 'body',
    index: scope.index ?? null,
    paragraph_index: anchor.paragraph_index,
    cell_path:
      scope.cell_path && scope.cell_path.length > 0 ? JSON.stringify(scope.cell_path) : null,
  });
}

/**
 * Return every run element belonging to the paragraph the given anchor
 * points at, sorted by run_index so the cumulative-offset walk reflects
 * document order.
 */
function findRunsInParagraph(container: HTMLElement, anchor: AnnotationCoordinate): HTMLElement[] {
  const { scope, paragraph_index } = anchor;
  const scopeKind = scope.kind ?? 'body';
  const expectedCellPath =
    scope.cell_path && scope.cell_path.length > 0 ? JSON.stringify(scope.cell_path) : null;

  const candidates = Array.from(
    container.querySelectorAll<HTMLElement>('[data-paragraph-index],[data-pid]')
  ).filter(el => {
    const pid = Number.parseInt(el.dataset.paragraphIndex ?? el.dataset.pid ?? '', 10);
    if (pid !== paragraph_index) return false;

    const scopeEl = el.closest<HTMLElement>('[data-scope-kind]');
    const elKind = scopeEl?.dataset.scopeKind ?? 'body';
    if (elKind !== scopeKind) return false;

    if (scope.index !== undefined) {
      const scopeIndexEl = el.closest<HTMLElement>('[data-scope-index]');
      const elIndex = scopeIndexEl?.dataset.scopeIndex;
      if (elIndex !== String(scope.index)) return false;
    }

    const elCellPath = normalizeCellPath(
      el.closest<HTMLElement>('[data-cell-path]')?.dataset.cellPath
    );
    return expectedCellPath === elCellPath;
  });

  return candidates.sort((a, b) => {
    const ra = Number.parseInt(a.dataset.runIndex ?? a.dataset.rid ?? '0', 10);
    const rb = Number.parseInt(b.dataset.runIndex ?? b.dataset.rid ?? '0', 10);
    return ra - rb;
  });
}

interface RunSpan {
  start: number;
  end: number;
  /** Null when the annotation continues in this run but the chip element
   *  lives in the start run — we still need to claim the text range here so
   *  the leftover `{{ … }}` / `{% … %}` characters don't bleed through. */
  chip: HTMLElement | null;
}

function placeChipsInParagraph(runs: HTMLElement[], annotations: AnnotationDraft[]): void {
  // Cumulative paragraph offset for each run (where in the merged paragraph
  // text this run starts). Sealed once per syncChips pass so the math stays
  // consistent across all annotations in this paragraph.
  const runOffsets: number[] = [];
  let cumulative = 0;
  for (const run of runs) {
    runOffsets.push(cumulative);
    cumulative += run.textContent.length;
  }

  const runSpans = new Map<HTMLElement, RunSpan[]>();
  for (const run of runs) runSpans.set(run, []);

  for (const annotation of annotations) {
    const startRunIdx = runs.findIndex(r => {
      const rid = Number.parseInt(r.dataset.runIndex ?? r.dataset.rid ?? '', 10);
      return rid === annotation.anchor.run_index;
    });
    if (startRunIdx === -1) continue;

    const offsetInRun = annotation.anchor.offset_in_run;
    const pStart = runOffsets[startRunIdx] + offsetInRun;

    // Extracted tags claim the full original_text footprint — possibly
    // crossing run boundaries. User-authored tags are zero-width inserts
    // that don't consume any source text.
    let pEnd = pStart;
    if (isExtracted(annotation)) {
      const original = annotation.origin_signature?.original_text ?? renderJinjaPreview(annotation);
      if (original) pEnd = pStart + original.length;
    }

    const chipEl = annotation.deleted ? null : buildChip(annotation);

    for (const [i, run] of runs.entries()) {
      const spans = runSpans.get(run);
      if (!spans) continue;

      const runText = run.textContent;
      const runStart = runOffsets[i];
      const runEnd = runStart + runText.length;

      // Zero-footprint insert: only the start run gets a span.
      if (pStart === pEnd) {
        if (i === startRunIdx) {
          spans.push({
            start: pStart - runStart,
            end: pStart - runStart,
            chip: chipEl,
          });
        }
        continue;
      }

      if (pEnd <= runStart || pStart >= runEnd) continue;
      spans.push({
        start: Math.max(0, pStart - runStart),
        end: Math.min(runText.length, pEnd - runStart),
        chip: i === startRunIdx ? chipEl : null,
      });
    }
  }

  for (const run of runs) {
    renderRunWithSpans(run, runSpans.get(run) ?? []);
  }
}

function renderRunWithSpans(runEl: HTMLElement, spans: RunSpan[]): void {
  const text = runEl.textContent;
  if (text.length === 0 && spans.length === 0) return;

  // Sort by start, then by end descending so a longer span at the same start
  // wraps shorter ones.
  spans.sort((a, b) => a.start - b.start || b.end - a.end);

  const children: Node[] = [];
  let cursor = 0;
  for (const span of spans) {
    const segmentStart = Math.max(cursor, span.start);
    if (segmentStart > cursor) {
      children.push(makeFragment(text.slice(cursor, segmentStart)));
    }
    if (span.chip) children.push(span.chip);
    cursor = Math.max(cursor, span.end);
  }
  if (cursor < text.length) {
    children.push(makeFragment(text.slice(cursor)));
  }

  runEl.replaceChildren(...children);
}

/**
 * Normalise a `cell_path` value (either the saved annotation's nested array
 * or the backend's `data-cell-path` attribute) to a single canonical form so
 * the comparison is independent of wire encoding.
 *
 * Wire formats we accept:
 *   - JSON nested array, e.g. `[[0,1,1]]` or `[[0,1,1],[0,2,3]]`
 *   - Flat comma-separated triple, e.g. `0,1,1`
 *   - Semicolon-separated triples for nested tables, e.g. `0,1,1;0,2,3`
 *   - Empty / missing → `null` (= body, no cell)
 */
function normalizeCellPath(raw: string | null | undefined): string | null {
  if (raw === null || raw === undefined) return null;
  const trimmed = raw.trim();
  if (trimmed === '') return null;

  // JSON-encoded nested array.
  try {
    const parsed = JSON.parse(trimmed);
    if (Array.isArray(parsed) && parsed.length > 0) {
      return JSON.stringify(parsed);
    }
    if (Array.isArray(parsed) && parsed.length === 0) return null;
  } catch {
    // Fall through to flat parsing.
  }

  // Flat triples — the backend emits `0,1,1` for a single cell and
  // (assumed) `0,1,1;0,2,3` for nested tables.
  const groups = trimmed.split(';').map(group =>
    group
      .split(',')
      .map(token => Number.parseInt(token.trim(), 10))
      .filter(value => !Number.isNaN(value))
  );
  if (groups.length === 0 || groups.some(group => group.length !== 3)) return null;
  return JSON.stringify(groups);
}

function isExtracted(annotation: AnnotationDraft): boolean {
  // Treat presence of `origin_signature` as a fallback signal in case the
  // backend hands back an extracted annotation without setting `origin` —
  // origin_signature is only ever populated for tags discovered in source.
  return annotation.origin === 'extracted' || annotation.origin_signature != null;
}

function buildChip(annotation: AnnotationDraft): HTMLElement {
  const chip = document.createElement('button');
  chip.type = 'button';
  chip.className = 'tag-chip';
  if (props.activeLocalIds?.includes(annotation.local_id)) chip.classList.add('tag-chip--active');
  if (isExtracted(annotation)) chip.classList.add('tag-chip--extracted');
  if (annotation.kind === 'block_start' || annotation.kind === 'block_end') {
    const { keyword } = parseBlockTag(annotation);
    chip.classList.add('tag-chip--block');
    chip.classList.add(`tag-chip--block-${keyword}`);
    if (annotation.kind === 'block_end') chip.classList.add('tag-chip--block-closing');
  } else if (annotation.binding?.kind === 'entity') {
    chip.classList.add('tag-chip--entity');
    chip.classList.add(`tag-chip--${annotation.binding.entity_type}`);
  } else if (annotation.binding?.kind === 'object') {
    chip.classList.add('tag-chip--object');
  }
  chip.dataset.localId = annotation.local_id;
  chip.textContent = renderJinjaPreview(annotation);
  return chip;
}

function makeFragment(text: string): HTMLElement {
  const fragment = document.createElement('span');
  fragment.className = 'tag-chip-text-fragment';
  fragment.textContent = text;
  return fragment;
}

function handleClick(event: MouseEvent): void {
  const target = event.target;
  if (!(target instanceof Element)) return;
  // Use closest() so we still resolve the chip when the user clicks a child
  // element (e.g. an icon we add later) rather than the button itself.
  const chip = target.closest<HTMLElement>('.tag-chip');
  if (!chip) return;
  const localId = chip.dataset.localId;
  if (!localId) return;
  const annotation = props.annotations.find(item => item.local_id === localId);
  if (!annotation) return;
  emit('click-tag', annotation, chip);
}

/**
 * Light the selected chips and, when asked, bring the first into view. Shared
 * by the selection watcher and by mount: the page hides this preview while
 * the field list is shown, so it often mounts after the selection already
 * changed, and the watcher alone would never scroll.
 */
function applyActive(ids: string[], scroll: boolean): void {
  const container = containerRef.value;
  if (!container) return;
  for (const chip of container.querySelectorAll<HTMLElement>('.tag-chip--active')) {
    chip.classList.remove('tag-chip--active');
  }
  let first: HTMLElement | null = null;
  for (const id of ids) {
    const chip = container.querySelector<HTMLElement>(`.tag-chip[data-local-id="${id}"]`);
    if (!chip) continue;
    chip.classList.add('tag-chip--active');
    first ??= chip;
  }
  if (scroll && first) {
    first.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }
}

onMounted(async () => {
  applyHtml(props.html);
  await syncChips();
  applyActive(props.activeLocalIds ?? [], true);
});

watch(
  () => props.html,
  html => {
    applyHtml(html);
    void syncChips();
  }
);
watch(
  () => props.annotations,
  () => void syncChips(),
  { deep: true }
);
// IMPORTANT: don't rebuild chips when the selection flips — just toggle the
// --active class on the affected chips. Rebuilding detaches the chip element
// the popover is pointing at, so its getBoundingClientRect() becomes
// (0, 0, 0, 0) and the popover lands at the viewport top-left on the first
// click after every active-state change.
watch(
  () => props.activeLocalIds ?? [],
  (nextIds, previousIds) => {
    // Bring the field into view when the selection moved to a different one;
    // a re-render that keeps the same first chip must not yank the scroll.
    applyActive(nextIds, nextIds[0] !== previousIds[0]);
  }
);
// --- chrome ------------------------------------------------------------------
const fsMeta = gaiaFontSize.meta;
const fwSemibold = String(gaiaFontWeight.semibold);
const trackBlockTag = gaiaTracking.blockTag;
const fsRow = gaiaFontSize.row;
// The chip palette, from tokens: violet = custom fields, green = entity
// inserts, amber = control-flow blocks, dashed amber = source-extracted tags
// not yet bound. The canvas typography mirrors the artboard's document page.
const surface = gaiaNeutral.surface;
const docText = gaiaNeutral.text;
const docInk = gaiaNeutral.ink;
const docBorder = gaiaNeutral.borderDivider;
const pageShadow = gaiaShadow.card;
const monoFont = gaiaFonts.mono;
const serifFont = "'Source Serif 4', Georgia, serif";
const customFg = gaiaTemplating.customFg;
const customBg = gaiaTemplating.customSurface;
const customBorder = gaiaTemplating.customBorder;
const entityFg = gaiaTemplating.entityFg;
const entityBg = gaiaTemplating.entitySurface;
const entityBorder = gaiaTemplating.entityBorder;
const blockFg = gaiaTemplating.blockFg;
const blockBg = gaiaTemplating.blockSurface;
const blockBorder = gaiaTemplating.blockBorder;
const onInk = gaiaNeutral.surface;
</script>

<template>
  <div ref="containerRef" class="annotated-preview" @click="handleClick" />
</template>

<style scoped>
.annotated-preview {
  position: relative;
  background: v-bind(surface);
  padding: 48px 64px;
  min-height: 100%;
  font-family: v-bind(serifFont);
  font-size: v-bind(fsRow);
  line-height: 1.9;
  color: v-bind(docText);
  overflow-y: auto;
  box-shadow: v-bind(pageShadow);
  border-radius: 4px;
}

/* Chips that the syncChips() pass injects into the rendered HTML. Violet is
   the default (custom fields and dates); the modifier classes below recolour
   by binding kind. */
.annotated-preview :deep(.tag-chip) {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 1px 8px;
  margin: 0 2px;
  background: v-bind(customBg);
  border: 1px solid v-bind(customBorder);
  border-radius: 6px;
  color: v-bind(customFg);
  font-size: v-bind(fsMeta);
  font-family: v-bind(monoFont);
  cursor: pointer;
  vertical-align: baseline;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease;
}

.annotated-preview :deep(.tag-chip:hover) {
  border-color: v-bind(customFg);
}

.annotated-preview :deep(.tag-chip--active) {
  background: v-bind(customFg);
  color: v-bind(onInk);
  border-color: v-bind(customFg);
}

/* Entity inserts — both kinds read green on the canvas; the palette's violet
   person tile is a navigation affordance, not a chip family. */
.annotated-preview :deep(.tag-chip--legal_entity),
.annotated-preview :deep(.tag-chip--natural_person) {
  background: v-bind(entityBg);
  border-color: v-bind(entityBorder);
  color: v-bind(entityFg);
}

.annotated-preview :deep(.tag-chip--legal_entity:hover),
.annotated-preview :deep(.tag-chip--natural_person:hover) {
  border-color: v-bind(entityFg);
}

.annotated-preview :deep(.tag-chip--legal_entity.tag-chip--active),
.annotated-preview :deep(.tag-chip--natural_person.tag-chip--active) {
  background: v-bind(entityFg);
  color: v-bind(onInk);
  border-color: v-bind(entityFg);
}

/* Tags discovered in the source .docx but not yet bound: dashed, so they read
   as "still to place" against the solid, committed chips. */
.annotated-preview :deep(.tag-chip--extracted) {
  border-style: dashed;
  background: v-bind(blockBg);
  border-color: v-bind(blockBorder);
  color: v-bind(blockFg);
}

.annotated-preview :deep(.tag-chip--extracted:hover) {
  border-color: v-bind(blockFg);
}

/* Block-control tags ({% if %}, {% for %}, {% endif %}, {% endfor %}) — one
   amber family for all of them; nesting reads through the muted closers. */
.annotated-preview :deep(.tag-chip--block) {
  font-style: italic;
  font-weight: v-bind(fwSemibold);
  letter-spacing: v-bind(trackBlockTag);
}

.annotated-preview :deep(.tag-chip--block-if),
.annotated-preview :deep(.tag-chip--block-elif),
.annotated-preview :deep(.tag-chip--block-else),
.annotated-preview :deep(.tag-chip--block-endif),
.annotated-preview :deep(.tag-chip--block-for),
.annotated-preview :deep(.tag-chip--block-endfor) {
  background: v-bind(blockBg);
  border-color: v-bind(blockBorder);
  color: v-bind(blockFg);
}

.annotated-preview :deep(.tag-chip--block-if:hover),
.annotated-preview :deep(.tag-chip--block-elif:hover),
.annotated-preview :deep(.tag-chip--block-else:hover),
.annotated-preview :deep(.tag-chip--block-endif:hover),
.annotated-preview :deep(.tag-chip--block-for:hover),
.annotated-preview :deep(.tag-chip--block-endfor:hover) {
  border-color: v-bind(blockFg);
}

/* Closing tags get a slightly muted look so a paragraph of nested blocks
   reads visually as "open + content + close". */
.annotated-preview :deep(.tag-chip--block-closing) {
  opacity: 0.75;
}

/* Light styling defaults for the rendered .docx HTML — backend's preview
   should already include semantic markup; this keeps it readable when run
   inside the panel. */
.annotated-preview :deep(p) {
  margin: 0 0 14px;
}

.annotated-preview :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin: 12px 0;
}

.annotated-preview :deep(td),
.annotated-preview :deep(th) {
  border: 1px solid v-bind(docBorder);
  padding: 6px 8px;
  vertical-align: top;
}

.annotated-preview :deep(h1),
.annotated-preview :deep(h2),
.annotated-preview :deep(h3) {
  margin: 20px 0 10px;
  font-weight: v-bind(fwSemibold);
  color: v-bind(docInk);
}
</style>
